<div class="cyber-profile-completion">
  <!-- Registration Error Notifications -->
  <app-registration-errors></app-registration-errors>

  <div class="flashy-card completion-card">
    <div class="cyber-card-header completion-header">
      <div class="completion-icon">
        @if (needsFirstName || needsLastName) {
          <i class="fas fa-user-edit"></i>
        }
        @if (!needsFirstName && !needsLastName) {
          <i class="fas fa-user-check"></i>
        }
      </div>

      <!-- Header for incomplete profile -->
      @if (
        needsFirstName || needsLastName || hasTierSpecificIncompleteFields()
        ) {
        <div>
          <h2>Complete Your Profile</h2>
          <p class="completion-description">
            Help us personalize your experience by completing your profile
            information.
            @if (isTrialOrPremium) {
              <span class="tier-requirement">
                <strong>{{ userTier }} tier</strong> requires complete profile
                information for account setup.
              </span>
            }
          </p>
        </div>
      }

      <!-- Header for complete profile -->
      @if (
        !needsFirstName &&
        !needsLastName &&
        !hasTierSpecificIncompleteFields()
        ) {
        <div>
          <h2>Profile Complete!</h2>
          <p class="completion-description">
            Great! Your profile information is complete for the
            <strong>{{ userTier }} tier</strong>. Click continue to proceed to the
            next step.
          </p>
        </div>
      }
    </div>

    <div class="completion-content">
      <!-- Server Message Display -->
      @if (serverMessage) {
        <div class="cyber-alert cyber-alert-danger" role="alert">
          <i class="fas fa-exclamation-triangle"></i>
          <div class="cyber-alert-content">
            <div class="cyber-alert-title">Error</div>
            <div class="cyber-alert-message">{{ serverMessage }}</div>
          </div>
        </div>
      }

      <!-- Profile Completion Form -->
      <form [formGroup]="profileForm" (ngSubmit)="onSubmit()" novalidate>
        <!-- Name Fields Row -->
        <div class="row">
          <!-- First Name -->
          <div class="col-md-6">
            <div class="cyber-input-wrapper mb-3">
              <label for="firstName" class="cyber-label">
                <span>First Name <span class="text-danger">*</span></span>
              </label>
              <input title="Form input" type="text" id="firstName" class="form-control cyber-input" [class.is-invalid]="isFieldInvalid('firstName')" formControlName="firstName"
                placeholder="Enter your first name" [readonly]="!needsFirstName" />
              <div class="invalid-feedback">
                {{ getFieldError("firstName") }}
              </div>
              @if (!needsFirstName) {
                <small class="form-text text-muted">
                  <i class="fas fa-check-circle text-success me-1"></i>
                  Already provided
                </small>
              }
            </div>
          </div>

          <!-- Last Name -->
          <div class="col-md-6">
            <div class="cyber-input-wrapper mb-3">
              <label for="lastName" class="cyber-label">
                Last Name <span class="text-danger">*</span>
              </label>
              <input title="Form input" type="text" id="lastName" class="form-control cyber-input" [class.is-invalid]="isFieldInvalid('lastName')" formControlName="lastName" placeholder="Enter your last name"
                [readonly]="!needsLastName" />
              <div class="invalid-feedback">
                {{ getFieldError("lastName") }}
              </div>
              @if (!needsLastName) {
                <small class="form-text text-muted">
                  <i class="fas fa-check-circle text-success me-1"></i>
                  Already provided
                </small>
              }
            </div>
          </div>
        </div>

        <!-- Phone Number -->
        <div class="cyber-input-wrapper mb-3">
          <label for="phone" class="cyber-label">
            Phone Number
            @if (isTrialOrPremium) {
              <span class="text-danger">*</span>
            }
            @if (!isTrialOrPremium) {
              <span class="text-muted">(Optional)</span>
            }
          </label>
          <input title="Form input" type="tel" id="phone" class="form-control cyber-input" [class.is-invalid]="isFieldInvalid('phone')" formControlName="phone" placeholder="Enter your phone number" />
          <div class="invalid-feedback">
            {{ getFieldError("phone") }}
          </div>
          <small class="form-text text-muted">
            <i class="fas fa-info-circle me-1"></i>
            @if (isTrialOrPremium) {
              <span>Required for {{ userTier }} tier account setup and support</span>
            }
            @if (!isTrialOrPremium) {
              <span>We'll only use this for important account notifications</span>
            }
          </small>
        </div>

        <!-- Job Title (Trial/Premium only) -->
        @if (isTrialOrPremium) {
          <div class="cyber-input-wrapper mb-3">
            <label for="jobTitle" class="cyber-label">
              Job Title <span class="text-danger">*</span>
            </label>
            <input title="Form input" type="text" id="jobTitle" class="form-control cyber-input" [class.is-invalid]="isFieldInvalid('jobTitle')" formControlName="jobTitle" placeholder="Enter your job title" />
            <div class="invalid-feedback">
              {{ getFieldError("jobTitle") }}
            </div>
            <small class="form-text text-muted">
              <i class="fas fa-info-circle me-1"></i>
              Required for {{ userTier }} tier professional account setup
            </small>
          </div>
        }

        <!-- Preferred Contact Method (Trial/Premium only) -->
        @if (isTrialOrPremium) {
          <div class="cyber-input-wrapper mb-3">
            <label for="preferredContactMethod" class="cyber-label">
              Preferred Contact Method <span class="text-danger">*</span>
            </label>
            <select title="Form select" id="preferredContactMethod" class="form-select cyber-input" [class.is-invalid]="isFieldInvalid('preferredContactMethod')" formControlName="preferredContactMethod">
              <option value="">Select preferred contact method</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="both">Both Email and Phone</option>
            </select>
            <div class="invalid-feedback">
              {{ getFieldError("preferredContactMethod") }}
            </div>
            <small class="form-text text-muted">
              <i class="fas fa-info-circle me-1"></i>
              How would you prefer to receive important notifications?
            </small>
          </div>
        }

        <!-- Current Email Display (Read-only) -->
        @if (user?.email) {
          <div class="cyber-input-wrapper mb-4">
            <label class="cyber-label" for="user-email-display">Email Address</label>
            <input title="Form input" type="email" id="user-email-display" class="form-control cyber-input" [value]="user?.email || ''" readonly />
            <small class="form-text text-muted">
              <i class="fas fa-lock me-1"></i>
              Your email address cannot be changed here
            </small>
          </div>
        }

        <!-- Show Invalid Inputs -->
        @if (profileForm.invalid) {
          <div class="text-danger me-3">
            Please fill out all required fields to continue.
          </div>
        }

        <!-- Action Buttons -->
        <div class="completion-actions">
          <!-- Profile Complete - Show Continue Button -->
          @if (
            !needsFirstName &&
            !needsLastName &&
            !hasTierSpecificIncompleteFields()
            ) {
            <div class="d-flex justify-content-center">
              <button type="button" class="btn btn-cyber-primary btn-lg" (click)="proceedToNextStep()">
                <i class="fas fa-arrow-right me-2"></i>
                Continue to Next Step
              </button>
            </div>
          }

          <!-- Profile Incomplete - Show Form Actions -->
          @if (
            needsFirstName ||
            needsLastName ||
            hasTierSpecificIncompleteFields()
            ) {
            <div class="d-flex justify-content-between align-items-center">
              <!-- Skip Button (only for free tier with optional fields) -->
              @if (!isTrialOrPremium && !needsFirstName && !needsLastName) {
                <button type="button" class="btn btn-cyber-secondary" (click)="onSkip()">
                  <i class="fas fa-arrow-right me-2"></i>
                  Skip Optional Fields
                </button>
              }
              @if (isTrialOrPremium || needsFirstName || needsLastName) {
                <div></div>
              }
              @if(profileForm.invalid) {
                <div class="text-danger me-3">
                  Please fill out all required fields to continue.
                </div>
              }
              <!-- Submit Button -->
              <button type="submit" class="btn btn-cyber-primary" [disabled]="loading || profileForm.invalid">
                @if (loading) {
                  <span class="spinner-border spinner-border-sm me-2"></span>
                }
                @if (!loading) {
                  <i class="fas fa-check me-2"></i>
                }
                {{ loading ? "Saving..." : "Complete Profile" }}
              </button>
            </div>
          }

          <!-- Tier Requirement Info -->
          @if (isTrialOrPremium) {
            <div class="tier-info-banner mt-3">
              <div class="cyber-alert cyber-alert-info">
                <i class="fas fa-info-circle"></i>
                <div class="cyber-alert-content">
                  <div class="cyber-alert-title">
                    {{ userTier }} Tier Requirements
                  </div>
                  <div class="cyber-alert-message">
                    {{ getTierRequirementText() }}
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </form>
    </div>

    <!-- Progress Indicator -->
    <div class="completion-progress">
      <div class="progress-steps">
        <div class="step completed">
          <i class="fas fa-user-check"></i>
          <span>Account Created</span>
        </div>
        <div class="step active">
          <i class="fas fa-user-edit"></i>
          <span>Profile Info</span>
        </div>
        <div class="step">
          <i class="fas fa-building"></i>
          <span>Company Setup</span>
        </div>
        <div class="step">
          <i class="fas fa-tachometer-alt"></i>
          <span>Dashboard</span>
        </div>
      </div>
    </div>
  </div>
</div>
